import type { Difficulty } from './types';

export type MatchOutcome = 'win' | 'lose' | 'draw';

export interface RoleRecord {
  played: number;
  wins: number;
  losses: number;
  draws: number;
}

export interface PlayerProfile {
  version: 1;
  roles: Record<string, RoleRecord>;
  solvedPuzzles: string[];
  dailyWins: string[];
}

export const PROFILE_KEY = 'cloud-gomoku-profile-v1';

export function emptyProfile(): PlayerProfile {
  return { version: 1, roles: {}, solvedPuzzles: [], dailyWins: [] };
}

export function roleRecord(profile: PlayerProfile, roleId: string): RoleRecord {
  return profile.roles[roleId] ?? { played: 0, wins: 0, losses: 0, draws: 0 };
}

export function recordMatch(profile: PlayerProfile, roleId: string, outcome: MatchOutcome): PlayerProfile {
  const current = roleRecord(profile, roleId);
  return {
    ...profile,
    roles: {
      ...profile.roles,
      [roleId]: {
        played: current.played + 1,
        wins: current.wins + (outcome === 'win' ? 1 : 0),
        losses: current.losses + (outcome === 'lose' ? 1 : 0),
        draws: current.draws + (outcome === 'draw' ? 1 : 0),
      },
    },
  };
}

export function recordPuzzle(profile: PlayerProfile, puzzleId: string, dailyKey?: string): PlayerProfile {
  return {
    ...profile,
    solvedPuzzles: profile.solvedPuzzles.includes(puzzleId) ? profile.solvedPuzzles : [...profile.solvedPuzzles, puzzleId],
    dailyWins: dailyKey && !profile.dailyWins.includes(dailyKey) ? [...profile.dailyWins, dailyKey] : profile.dailyWins,
  };
}

export function mastery(profile: PlayerProfile, difficulty: Difficulty): { points: number; label: string } {
  const record = roleRecord(profile, difficulty.id);
  const points = Math.min(100, record.played * 4 + record.wins * 14 + profile.solvedPuzzles.length * 3);
  const label = points >= 80 ? '守局心得' : points >= 50 ? '熟習棋路' : points >= 20 ? '初識對手' : '初次相逢';
  return { points, label };
}

export function loadProfile(storage: Pick<Storage, 'getItem' | 'removeItem'> = localStorage): PlayerProfile {
  try {
    const raw = storage.getItem(PROFILE_KEY);
    if (!raw) return emptyProfile();
    const parsed = JSON.parse(raw) as PlayerProfile;
    if (parsed.version !== 1 || !parsed.roles || !Array.isArray(parsed.solvedPuzzles) || !Array.isArray(parsed.dailyWins)) return emptyProfile();
    return parsed;
  } catch {
    storage.removeItem(PROFILE_KEY);
    return emptyProfile();
  }
}

export function saveProfile(profile: PlayerProfile, storage: Pick<Storage, 'setItem'> = localStorage): void {
  storage.setItem(PROFILE_KEY, JSON.stringify(profile));
}
